import React, {
  useState,
  useEffect,
  useMemo,
} from 'react';

import { motion } from 'framer-motion';
import { useAppConfig } from '../ConfigContext';

const LAST_ROW_HEIGHT = 116;


/* =========================================================
   TIME
   ========================================================= */

function getNextUtcMidnight() {
  const d = new Date();

  return Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate() + 1,
    0,
    0,
    0,
    0
  );
}


function formatCountdown(ms) {
  const total = Math.max(
    0,
    Math.floor(ms / 1000)
  );

  const h = String(
    Math.floor(total / 3600)
  ).padStart(2, '0');

  const m = String(
    Math.floor(
      (total % 3600) / 60
    )
  ).padStart(2, '0');

  return `${h}h:${m}m`;
}


/* =========================================================
   ITEM HELPERS
   Same mappings used by ItemsInventory
   ========================================================= */

function normalizePrizeType(type) {
  const s = String(type ?? '')
    .trim()
    .toLowerCase();

  if (
    [
      'cardpieces',
      'cardpiece',
      'card_pieces',
      'card-pieces',
    ].includes(s)
  ) {
    return 'cardPieces';
  }

  if (
    [
      'spin',
      'spins',
      'fortune_spin',
      'fortune_spins',
      'fortune-wheel',
      'fortune_wheel',
    ].includes(s)
  ) {
    return 'spins';
  }

  if (
    [
      'seasonalaccessory',
      'seasonal_accessory',
      'seasonal-accessory',
      'skin',
      'skins',
    ].includes(s)
  ) {
    return 'seasonal_accessory';
  }

  if (
    [
      'name',
      'cosmetic',
      'name_cosmetic',
    ].includes(s)
  ) {
    return 'name';
  }

  if (
    ['rune', 'runes'].includes(s)
  ) {
    return 'runes';
  }

  if (
    ['spark', 'sparks'].includes(s)
  ) {
    return 'sparks';
  }

  if (
    ['booster', 'boosters'].includes(s)
  ) {
    return 'booster';
  }

  if (
    ['ability', 'abilities'].includes(s)
  ) {
    return 'ability';
  }

  if (
    ['card', 'cards'].includes(s)
  ) {
    return 'card';
  }

  if (s === 'tapi') {
    return 'tapi';
  }

  if (
    ['evolve', 'evolvement'].includes(s)
  ) {
    return 'evolve';
  }

  return s;
}


function getPrizeRarityIcon(rarity) {
  const r = String(rarity || '')
    .trim()
    .toLowerCase();

  if (r === 'legendary') {
    return '/legendary.png';
  }

  if (r === 'epic') {
    return '/epic.png';
  }

  if (r === 'rare') {
    return '/rare.png';
  }

  return '/common.png';
}


function getMainPrizeIcon(prize) {
  if (!prize) {
    return '/pack.png';
  }

  const type = normalizePrizeType(
    prize.type
  );

  switch (type) {
    case 'runes':
      return '/runes.png';

    case 'sparks':
      return '/sparks.png';

    case 'booster':
      return '/energy.png';

    case 'spins':
      return '/fortuneicon.png';

    case 'tapi':
      return '/icon_tapi.png';

      case 'ticket':
  return '/ticket.png';


    case 'evolve':
      return '/evolveicon.png';

    case 'seasonal_accessory':
      return '/fragment.png';

    case 'shield':
      return '/shield.png';

    case 'ability':
      return '/fightlog.png';

    case 'gamepass':
      return '/passicon.png';

    case 'cardPieces':
      return '/pieces.png';

    case 'card':
      return getPrizeRarityIcon(
        prize.cardRarity
      );

    case 'name': {
      const category = String(
        prize.nameCategory || ''
      )
        .trim()
        .toLowerCase();

      const value = String(
        prize.nameValue || ''
      ).trim();

      if (value) {
        if (
          value.startsWith('/') ||
          value.startsWith('http://') ||
          value.startsWith('https://')
        ) {
          return value;
        }

        return `/name-cosmetics/${value}`;
      }

      if (
        category === 'banners' ||
        category === 'banner'
      ) {
        return '/banner_icon.png';
      }

      if (
        category === 'emogy' ||
        category === 'emoji' ||
        category === 'emojy'
      ) {
        return '/emogy_icon.png';
      }

      return '/style_icon.png';
    }

    default:
      return '/pack.png';
  }
}


function getMainPrizeName(prize) {
  if (!prize) {
    return 'Main Prize';
  }

  const type = normalizePrizeType(
    prize.type
  );

  if (
    type === 'sparks' &&
    Number(prize.sparksAmount) > 0
  ) {
    return `${Number(
      prize.sparksAmount
    ).toLocaleString()} Sparks`;
  }

  if (
    type === 'runes' &&
    Number(prize.runesAmount) > 0
  ) {
    return `${Number(
      prize.runesAmount
    ).toLocaleString()} Runes`;
  }

  if (
    type === 'tapi' &&
    Number(prize.tapiAmount) > 0
  ) {
    return `${Number(
      prize.tapiAmount
    ).toLocaleString()} Tapi`;
  }

  return (
    prize.name ||
    'Main Prize'
  );
}


/* =========================================================
   MODAL
   ========================================================= */

const DailyCheckInModal = ({
  onClose,

  streak = 0,

  claimedDays = [],
  missedDays = [],

  cycleMissed = false,
  allDaysClaimed = false,

  mainPrize = null,
  mainPrizeGranted = false,
}) => {
  const [loading, setLoading] =
    useState(true);

  const [checkedIn, setCheckedIn] =
    useState(false);

  const [now, setNow] =
    useState(Date.now());

  const { daily } =
    useAppConfig();


  /* =======================================================
     CLAIMED / MISSED SETS
     ======================================================= */

  const claimedSet = useMemo(
    () =>
      new Set(
        (
          Array.isArray(claimedDays)
            ? claimedDays
            : []
        ).map(Number)
      ),
    [claimedDays]
  );

  const missedSet = useMemo(
    () =>
      new Set(
        (
          Array.isArray(missedDays)
            ? missedDays
            : []
        ).map(Number)
      ),
    [missedDays]
  );


  /* =======================================================
     DAILY REWARDS
     ======================================================= */

  const days = useMemo(() => {
    if (
      !Array.isArray(daily?.rewards) ||
      daily.rewards.length !== 7
    ) {
      return [];
    }

    return daily.rewards
      .slice()
      .sort(
        (a, b) =>
          Number(a.day) -
          Number(b.day)
      )
      .map((r, i) => ({
        label:
          `DAY ${Number(r.day) || i + 1}`,

        icon:
          String(r.type).toLowerCase() ===
          'runes'
            ? '/runes.png'
            : '/sparks.png',

        amount:
          Number(r.amount) || 0,

        kind:
          String(
            r.type || 'sparks'
          ).toLowerCase(),
      }));
  }, [daily]);


  /* =======================================================
     OPEN ANIMATION
     ======================================================= */

  useEffect(() => {
    const timer =
      setTimeout(() => {
        setLoading(false);
        setCheckedIn(true);
      }, 1000);

    return () =>
      clearTimeout(timer);
  }, []);


  /* =======================================================
     COUNTDOWN
     ======================================================= */

  useEffect(() => {
    const timer =
      setInterval(() => {
        setNow(Date.now());
      }, 1000);

    return () =>
      clearInterval(timer);
  }, []);


  /* =======================================================
     STREAK POSITION
     ======================================================= */

  const safeStreak =
    Math.max(
      1,
      Math.min(
        Number(streak) || 1,
        days.length || 7
      )
    );

  const justCheckedIndex =
    checkedIn
      ? safeStreak - 1
      : -1;

  const nextDayIndex =
    checkedIn
      ? safeStreak % 7
      : safeStreak - 1;

  const today =
    justCheckedIndex >= 0
      ? days[justCheckedIndex]
      : null;

  const countdownText =
    formatCountdown(
      getNextUtcMidnight() -
      now
    );


  /* =======================================================
     MAIN PRIZE STATUS
     ======================================================= */

  /*
    Backend is authoritative.

    mainPrizeGranted means it ACTUALLY
    awarded/found the Main Prize.
  */
  const mainPrizeCompleted =
    checkedIn &&
    mainPrizeGranted === true;

  /*
    Any missed day permanently kills
    the Main Prize for THIS cycle.
  */
  const mainPrizeLost =
    checkedIn &&
    cycleMissed === true &&
    mainPrizeGranted !== true;

  /*
    Day 6, perfect cycle so far:
    tomorrow's Day 7 can unlock it.
  */
  const mainPrizeAlmostReady =
    checkedIn &&
    safeStreak === 6 &&
    cycleMissed !== true &&
    mainPrizeGranted !== true;

  /*
    Defensive state.

    Backend says all 7 were collected
    but animation/main-prize response is
    still settling.
  */
  const perfectCycle =
    allDaysClaimed === true &&
    cycleMissed !== true;


  /* =======================================================
     INVALID CONFIG
     ======================================================= */

  if (!days.length) {
    return (
      <div className="modal_box">

        <motion.div
          className="modal fancy-success-modal"
          style={{
            padding: 15,
          }}
          initial={{
            scale: 0.8,
            opacity: 0,
          }}
          animate={{
            scale: 1,
            opacity: 1,
          }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20,
          }}
        >

          <h2 className="modal-title">
            Daily rewards unavailable
          </h2>

          <div
            className="text-center"
            style={{
              fontSize: 18,
              color: '#ddd',
              textShadow:
                '1px 1px #000',
              marginTop: 8,
              marginBottom: 12,
            }}
          >
            Daily rewards must be
            configured in Admin.
          </div>

          <div className="modal-footer">
            <button onClick={onClose}>
              Close
            </button>
          </div>

        </motion.div>

      </div>
    );
  }


  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <div className="modal_box">


      <div className="modal_title">

        Daily Rewards

        <button
          type="button"
          onClick={onClose}
          className="modal_close"
        />

      </div>


      <motion.div
        className="modal_body"
        initial={{
          scale: 0.8,
          opacity: 0,
        }}
        animate={{
          scale: 1,
          opacity: 1,
        }}
        exit={{
          scale: 0.92,
          opacity: 0,
        }}
      >

        <div
          className="modal_inside"
          style={{
            paddingTop: 15,
          }}
        >


          {/* TITLE */}

          {loading ? (

            <div
              className="shadow2"
              style={{
                fontWeight: 600,
                fontSize: 28,
              }}
            >
              Claiming Reward…
            </div>

          ) : mainPrizeCompleted ? (

            <div
              className="shadow2"
              style={{
                fontWeight: 600,
                fontSize: 25,
                marginBottom: 10,
              }}
            >
              7-DAY STREAK COMPLETE!
            </div>

          ) : (

            <div
              className="shadow2"
              style={{
                fontWeight: 600,
                fontSize: 28,
                marginBottom: 10,
              }}
            >
              Check-in Complete!
            </div>

          )}


          <div
            className="checkday-grid"
            style={{
              margin: 0,
            }}
          >

            <div className="info">


              {/* RECEIVED MESSAGE */}

              {today && (

                <div
                  className="shadow1"
                  style={{
                    float: 'left',
                    width: '100%',
                    boxSizing:
                      'border-box',
                    marginBottom: 5,
                    textAlign: 'center',
                  }}
                >

                  You received{' '}

                  <strong>
                    +{today.amount}
                  </strong>{' '}

                  <img
                    src={
                      today.kind === 'runes'
                        ? '/runes.png'
                        : '/sparks.png'
                    }
                    alt={today.kind}
                    style={{
                      height: 21,
                      verticalAlign:
                        'text-bottom',
                    }}
                  />{' '}

                  {today.kind}

                  {mainPrizeCompleted &&
                  mainPrize ? (
                    <>
                      {' '}+{' '}

                      <strong>
                        {getMainPrizeName(
                          mainPrize
                        )}
                      </strong>

                      !
                    </>
                  ) : (
                    '.'
                  )}

                </div>

              )}


              {/* DAYS 1 - 7 */}

              {days.map((d, i) => {

                const dayNumber =
                  i + 1;

                /*
                  THIS is the important change.

                  We no longer assume:
                    i <= streak = claimed.

                  Backend tells us exactly which
                  days were actually claimed.
                */
                const isChecked =
                  checkedIn &&
                  claimedSet.has(
                    dayNumber
                  );

                const isMissed =
                  checkedIn &&
                  missedSet.has(
                    dayNumber
                  );

                const isJustChecked =
                  checkedIn &&
                  dayNumber ===
                    safeStreak &&
                  isChecked;

                const isNext =
                  checkedIn &&
                  !isChecked &&
                  !isMissed &&
                  i ===
                    nextDayIndex;

                const isLocked =
                  checkedIn &&
                  !isChecked &&
                  !isMissed &&
                  !isNext;


                return (

                  <motion.div
                    key={d.label}

                    className={
                      `checkday ${
                        isChecked
                          ? 'checked'
                          : ''
                      } ${
                        isJustChecked
                          ? 'just-checked'
                          : ''
                      } ${
                        isNext
                          ? 'next-day'
                          : ''
                      } ${
                        isLocked
                          ? 'locked'
                          : ''
                      } ${
                        isMissed
                          ? 'missed'
                          : ''
                      }`
                    }

                    style={{
                      position:
                        'relative',

                      /*
                       * DAY 7 gets same
                       * height as Main Prize.
                       */
                      height:
                        i === 6
                          ? LAST_ROW_HEIGHT
                          : undefined,

                      background:
                        isMissed
                          ? 'linear-gradient(180deg, #351010 0%, #130505 100%)'
                          : isLocked
                            ? 'linear-gradient(180deg, #151515 0%, #050505 100%)'
                            : undefined,

                      opacity:
                        isMissed
                          ? 0.7
                          : isLocked
                            ? 0.82
                            : 1,

                      padding: 9,
                    }}

                    initial={false}

                    animate={
                      isJustChecked
                        ? {
                            scale: [
                              1,
                              1.08,
                              1,
                            ],
                          }
                        : {
                            scale: 1,
                          }
                    }

                    transition={{
                      duration: 0.35,
                    }}

                    aria-checked={
                      isChecked
                    }

                    role="checkbox"
                  >

                    <span>
                      {d.label}
                    </span>


                    <img
                      src={d.icon}
                      alt={d.kind}
                      draggable={false}
                      style={{
                        height: 32,

                        objectFit:
                          'contain',

                        filter:
                          isMissed
                            ? 'grayscale(1)'
                            : undefined,
                      }}
                    />


                    {isMissed ? (

                      <b
                        style={{
                          width:
                            '100%',

                          fontSize:
                            10,

                          color:
                            '#ff7777',

                          lineHeight:
                            '24px',

                          textAlign:
                            'center',

                          textShadow:
                            '1px 1px #000',
                        }}
                      >
                        MISSED
                      </b>

                    ) : isChecked ? (

                      <b>
                        <img
                          src="/v2/check.png"
                          alt="checked"
                        />
                      </b>

                    ) : isNext ? (

                      <b
                        style={{
                          width:
                            '100%',

                          height:
                            24,

                          margin:
                            '3px 0 0 0',

                          padding:
                            0,

                          borderRadius:
                            12,

                          background:
                            '#DF5600',

                          boxSizing:
                            'border-box',

                          overflow:
                            'hidden',

                          display:
                            'flex',

                          alignItems:
                            'center',

                          justifyContent:
                            'center',
                        }}
                      >

                        <span
                          style={{
                            float:
                              'none',

                            width:
                              'auto',

                            margin:
                              0,

                            display:
                              'inline-flex',

                            alignItems:
                              'center',

                            justifyContent:
                              'center',

                            whiteSpace:
                              'nowrap',

                            color:
                              '#fff',

                            fontSize:
                              10,

                            lineHeight:
                              1,

                            fontWeight:
                              600,

                            textShadow:
                              '1px 1px 0 #000',
                          }}
                        >

                          <img
                            src="/v2/clock2.png"
                            alt=""
                            style={{
                              width:
                                16,

                              height:
                                16,

                              objectFit:
                                'contain',

                              marginRight:
                                -1,
                            }}
                          />

                          {countdownText}

                        </span>

                      </b>

                    ) : (

                      <b>
                        <img
                          src="/v2/lock.png"
                          alt="locked"
                        />
                      </b>

                    )}

                  </motion.div>

                );
              })}


              {/* MAIN PRIZE */}

              {mainPrize && (

                <motion.div
                  className={
                    `checkday main-prize ${
                      mainPrizeCompleted
                        ? 'checked just-checked'
                        : 'locked'
                    }`
                  }

                  style={{
                    width:
                      'calc((200% / 3) - 10px)',

                    height:
                      LAST_ROW_HEIGHT,

                    minWidth:
                      0,

                    position:
                      'relative',

                    boxSizing:
                      'border-box',

                    padding:
                      9,

                    background:
                      mainPrizeCompleted
                        ? undefined

                        : mainPrizeLost
                          ? 'linear-gradient(180deg, #351010 0%, #130505 100%)'

                          : mainPrizeAlmostReady
                            ? 'linear-gradient(180deg, #2d210b 0%, #110c03 100%)'

                            : 'linear-gradient(180deg, #151515 0%, #050505 100%)',

                    opacity:
                      mainPrizeCompleted
                        ? 1

                        : mainPrizeAlmostReady
                          ? 0.95

                          : mainPrizeLost
                            ? 0.72

                            : 0.82,
                  }}

                  initial={false}

                  animate={
                    mainPrizeCompleted
                      ? {
                          scale: [
                            1,
                            1.04,
                            1,
                          ],
                        }
                      : {
                          scale: 1,
                        }
                  }

                  transition={{
                    duration:
                      0.4,
                  }}
                >


                  <span>
                    MAIN PRIZE
                  </span>


                  <div
                    style={{
                      float:
                        'left',

                      width:
                        '100%',

                      height:
                        67,

                      display:
                        'flex',

                      alignItems:
                        'center',

                      justifyContent:
                        'center',

                      gap:
                        8,

                      boxSizing:
                        'border-box',

                      padding:
                        '0 4px',
                    }}
                  >


                    {/* ITEM ICON */}

                    <img
                      src={
                        getMainPrizeIcon(
                          mainPrize
                        )
                      }

                      alt={
                        getMainPrizeName(
                          mainPrize
                        )
                      }

                      draggable={
                        false
                      }

                      onError={(e) => {
                        e.currentTarget.onerror =
                          null;

                        e.currentTarget.src =
                          '/pack.png';
                      }}

                      style={{
                        width:
                          42,

                        height:
                          42,

                        flexShrink:
                          0,

                        objectFit:
                          'contain',

                        filter:
                          mainPrizeLost
                            ? 'grayscale(1) drop-shadow(0 2px 3px rgba(0,0,0,0.7))'
                            : 'drop-shadow(0 2px 3px rgba(0,0,0,0.7))',
                      }}
                    />


                    {/* PRIZE INFO */}

                    <div
                      style={{
                        minWidth:
                          0,

                        flex:
                          1,

                        textAlign:
                          'left',
                      }}
                    >

                      <div
                        className="shadow1"
                        style={{
                          color:
                            '#fff',

                          fontSize:
                            13,

                          fontWeight:
                            700,

                          lineHeight:
                            '14px',

                          whiteSpace:
                            'nowrap',

                          overflow:
                            'hidden',

                          textOverflow:
                            'ellipsis',
                        }}
                      >
                        {getMainPrizeName(
                          mainPrize
                        )}
                      </div>


                      <div
                        className="shadow1"
                        style={{
                          color:
                            mainPrizeLost
                              ? '#ff7777'
                              : '#fff',

                          fontSize:
                            9,

                          fontWeight:
                            700,

                          lineHeight:
                            '10px',

                          marginTop:
                            3,
                        }}
                      >
                        {
                          mainPrizeCompleted
                            ? 'CLAIMED'

                            : mainPrizeLost
                              ? 'MISSED THIS CYCLE'

                              : mainPrizeAlmostReady
                                ? 'COMPLETE DAY 7'

                                : perfectCycle
                                  ? 'COMPLETE DAY 7'

                                  : 'COMPLETE 7 DAYS'
                        }
                      </div>

                    </div>


                    {/* LOCK / CHECK */}

                    <div
                      style={{
                        width:
                          28,

                        flexShrink:
                          0,

                        display:
                          'flex',

                        alignItems:
                          'center',

                        justifyContent:
                          'center',
                      }}
                    >

                      <img
                        src={
                          mainPrizeCompleted
                            ? '/v2/check.png'
                            : '/v2/lock.png'
                        }

                        alt={
                          mainPrizeCompleted
                            ? 'claimed'
                            : 'locked'
                        }

                        style={{
                          height:
                            24,

                          maxWidth:
                            28,

                          objectFit:
                            'contain',

                          opacity:
                            mainPrizeLost
                              ? 0.65
                              : 1,
                        }}
                      />

                    </div>

                  </div>

                </motion.div>

              )}


              <div
                style={{
                  clear:
                    'both',
                }}
              />

            </div>

          </div>

        </div>


        <div className="modal_footer">

          <button
            type="button"
            className="game-button red-button"
            onClick={onClose}
          >
            CLOSE
          </button>

        </div>

      </motion.div>

    </div>
  );
};

export default DailyCheckInModal;